import { blueGrey } from "@mui/material/colors";
import { createTheme } from "@mui/material/styles";

export const projectName = "Kewordal";

export const snackBarPlacement = { horizontal: "left", vertical: "bottom" };

const primaryColor = "#01AAF5";
const successColor = "#8ac246";
const secondaryColor = blueGrey["700"];
const tableHeadColor = "#7ad1f8";
const tooltipBgColor = "#424242";
const fontFamily = '"Roboto", sans-serif';
const rem = (px) => `${px / 16}rem`;
const actionButtonHeight = `clamp(${rem(38)}, 2.8vw, ${rem(42)})`;
const actionButtonPadding = `0 clamp(${rem(14)}, 1vw, ${rem(18)})`;
const actionButtonFontSize = `clamp(${rem(13)}, 0.9vw, ${rem(15)})`;
const actionButtonIconSize = `clamp(${rem(17)}, 1.2vw, ${rem(20)})`;
const listButtonShadow = `0 ${rem(3)} ${rem(8)} rgba(0, 0, 0, 0.22)`;
const listButtonHoverShadow = `0 ${rem(4)} ${rem(10)} rgba(0, 0, 0, 0.24)`;
const dropdownHoverColor = "#f4f4f6";
const dropdownSelectedColor = "#e3f6ff";
const dropdownItemRadius = rem(8);
const dropdownItemHeight = rem(48);
const scrollbarButtonReset = {
  WebkitAppearance: "none !important",
  appearance: "none !important",
  display: "none !important",
  width: "0 !important",
  height: "0 !important",
  minWidth: "0 !important",
  minHeight: "0 !important",
  background: "transparent !important",
  border: "0 !important",
};

export const theme = createTheme({
  typography: {
    fontFamily,
  },
  palette: {
    primary: {
      main: primaryColor,
    },
    secondary: {
      main: secondaryColor,
    },
    success: {
      main: successColor,
    },
    warning: {
      main: "#FEC004",
    },
  },
  components: {
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: rem(14),
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)",
          border: "1px solid #eef0f3",
          padding: rem(6),
        },
        list: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: rem(2),
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: dropdownItemHeight,
          borderRadius: dropdownItemRadius,
          padding: `${rem(12)} ${rem(14)}`,
          color: "#111111",
          fontSize: rem(15),
          fontWeight: 400,
          lineHeight: 1.2,
          "&:hover": {
            backgroundColor: `${dropdownHoverColor} !important`,
            color: "inherit",
          },
          "&.Mui-focusVisible": {
            backgroundColor: `${dropdownHoverColor} !important`,
            color: "inherit",
          },
          "&.Mui-selected": {
            backgroundColor: `${dropdownSelectedColor} !important`,
            color: "inherit",
          },
          "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
            backgroundColor: `${dropdownSelectedColor} !important`,
            color: "inherit",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          marginTop: rem(6),
          borderRadius: rem(14),
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)",
          border: "1px solid #eef0f3",
          overflow: "hidden",
        },
        listbox: {
          padding: rem(6),
          display: "flex",
          flexDirection: "column",
          gap: rem(2),
          "& .MuiAutocomplete-option": {
            minHeight: dropdownItemHeight,
            borderRadius: dropdownItemRadius,
            padding: `${rem(12)} ${rem(14)}`,
            color: "#111111",
            fontSize: rem(15),
            fontWeight: 400,
            lineHeight: 1.2,
            '&[data-focus="true"]': {
              backgroundColor: `${dropdownHoverColor} !important`,
              color: "inherit",
            },
            '&[aria-selected="true"]': {
              backgroundColor: `${dropdownSelectedColor} !important`,
              color: "inherit",
            },
            '&[aria-selected="true"][data-focus="true"]': {
              backgroundColor: `${dropdownSelectedColor} !important`,
              color: "inherit",
            },
          },
        },
        option: {
          minHeight: dropdownItemHeight,
          borderRadius: dropdownItemRadius,
          padding: `${rem(12)} ${rem(14)}`,
          color: "#111111",
          fontSize: rem(15),
          fontWeight: 400,
          lineHeight: 1.2,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: actionButtonHeight,
          height: actionButtonHeight,
          borderRadius: rem(999),
          padding: actionButtonPadding,
          fontSize: actionButtonFontSize,
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: 0,
          textTransform: "none",
          whiteSpace: "nowrap",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          "& .MuiButton-startIcon, & .MuiButton-endIcon": {
            margin: 0,
          },
          "& .MuiButton-startIcon": {
            marginRight: rem(7),
          },
          "& .MuiButton-endIcon": {
            marginLeft: rem(7),
          },
          "& svg, & .MuiSvgIcon-root": {
            width: actionButtonIconSize,
            height: actionButtonIconSize,
            fontSize: actionButtonIconSize,
            flex: "0 0 auto",
          },
        },
        contained: {
          color: "white",
          borderRadius: rem(999),
        },
        containedPrimary: {
          backgroundColor: "#09aff4",
          boxShadow: listButtonShadow,
          "&:hover": {
            backgroundColor: "#09a7ea",
            boxShadow: listButtonHoverShadow,
            transform: "none",
          },
        },
        outlined: {
          borderRadius: rem(999),
        },
        outlinedPrimary: {
          color: "#111111",
          borderColor: "transparent",
          backgroundColor: "#ffffff",
          boxShadow: `0 2px ${rem(7)} rgba(0, 0, 0, 0.08)`,
          "&:hover": {
            borderColor: "transparent",
            backgroundColor: "#f4f4f4",
            boxShadow: `0 2px ${rem(7)} rgba(0, 0, 0, 0.08)`,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: tableHeadColor,
          color: "white",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& > .MuiBackdrop-root": {
            backgroundColor: "rgba(0,0,0,.2)",
            backdropFilter: `blur(${rem(15)})`,
          },
        },
        paper: {
          position: "relative",
          borderRadius: rem(24),
          background: "#fff",
          boxShadow: `0 ${rem(8)} ${rem(32)} rgba(0,0,0,0.18)`,
          overflow: "hidden",
          "&::before, &::after": {
            content: '""',
            position: "absolute",
            width: rem(180),
            height: rem(180),
            borderRadius: rem(32),
            zIndex: -1,
            pointerEvents: "none",
          },
          "&::before": {
            top: rem(-10),
            right: rem(-10),
            background: "#FEC004",
            boxShadow: `0 ${rem(2)} ${rem(12)} rgba(255,167,38,0.18)`,
          },
          "&::after": {
            bottom: rem(-10),
            left: rem(-10),
            background: "#8AC246",
            boxShadow: `0 ${rem(2)} ${rem(12)} rgba(102,187,106,0.18)`,
          },
          "&.MuiDialog-paperFullScreen": {
            borderRadius: 0,
            boxShadow: "none",
            overflow: "auto",
            "&::before, &::after": {
              display: "none",
            },
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          scrollbarGutter: "stable",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0,0,0,0.28) transparent",
          "&::-webkit-scrollbar": {
            width: rem(7),
            height: rem(7),
          },
          "&::-webkit-scrollbar-track": {
            borderRadius: rem(999),
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: rem(999),
            backgroundColor: "rgba(0,0,0,0.28)",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(0,0,0,0.36)",
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          "& .MuiTooltip-tooltip": {
            backgroundColor: tooltipBgColor,
            fontSize: rem(12),
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*::-webkit-scrollbar-button": scrollbarButtonReset,
        "*::-webkit-scrollbar-button:single-button": scrollbarButtonReset,
        "*::-webkit-scrollbar-button:double-button": scrollbarButtonReset,
        "*::-webkit-scrollbar-button:vertical:start": scrollbarButtonReset,
        "*::-webkit-scrollbar-button:vertical:end": scrollbarButtonReset,
        "*::-webkit-scrollbar-button:horizontal:start": scrollbarButtonReset,
        "*::-webkit-scrollbar-button:horizontal:end": scrollbarButtonReset,
        "*::-webkit-scrollbar-button:increment": scrollbarButtonReset,
        "*::-webkit-scrollbar-button:decrement": scrollbarButtonReset,
        ".MuiAutocomplete-listbox::-webkit-scrollbar-button": scrollbarButtonReset,
        ".MuiMenu-list::-webkit-scrollbar-button": scrollbarButtonReset,
        ".MuiDialogContent-root::-webkit-scrollbar-button": scrollbarButtonReset,
        html: {
          fontFamily,
          fontSize: "clamp(14px, 0.2vw + 12.2px, 16px)",
          textSizeAdjust: "100%",
        },
        body: {
          fontFamily,
        },
      },
    },
  },
});
